// ─────────────────────────────────────────────────────────────────
// Phase B.1.b — Modale suggestion d'analyses Ortho/MGD
// Date : 2026-05-09
//
// Workflow :
// 1. A l'ouverture, charge le catalogue lab_tests depuis Supabase
// 2. Appelle l'IA suggestAnalyses() avec anamnese pseudonymisee
// 3. Affiche les 3-5 suggestions avec checkboxes
// 4. Anissa coche/decoche -> marge calculee live
// 5. Validation -> appelle onValidate(plan) + ferme modale
// ─────────────────────────────────────────────────────────────────

import { useState, useEffect, useId, useMemo, useRef } from 'react';
import { supabase } from './supabaseClient';
import { suggestAnalyses, enrichSuggestionsWithCatalog } from './services/suggestAnalyses';
import { getPackAccess, PACK_DEFINITIONS } from './services/packSystem';
import { computeLabFees, UNKNOWN_SPECIMEN, PER_PRESCRIPTION } from './services/labFees';
import { calculerCoutFacture } from './services/coutAnalyses';
import { nombreDeBonsPour } from './services/clinical/catalog/orthoBonsDeCommande';
import BonsACocher from './components/BonsACocher';
import LabBadge, { LAB_BADGES } from './components/LabBadge';

// V97.143 : les labos entre lesquels Anissa arbitre pour une cliente donnée.
// Ortho-Analytic et MGD sont deux partenaires à égalité : l'égalité tient au
// traitement visuel identique des trois boutons, pas à leur ordre (qui suit
// la déclaration de LAB_BADGES et n'est qu'une convention).
const LAB_CHOICES = [
  { value: 'both', label: 'Les deux' },
  { value: 'ortho_analytic', label: 'Ortho' },
  { value: 'mgd', label: 'MGD' },
];
const ALLOWED_LABS = new Set(['ortho_analytic', 'mgd']);

export function manualCatalogCandidates(catalog, labChoice = 'both', query = '') {
  const needle = String(query || '').trim().toLocaleLowerCase('fr-CH');
  return (Array.isArray(catalog) ? catalog : [])
    .filter((test) => manualSelectionEntry(test) !== null)
    .filter((test) => labChoice === 'both' || test?.source_lab === labChoice)
    .filter((test) => {
      if (!needle) return true;
      return [test?.display_name, test?.code, test?.category]
        .some((value) => String(value || '').toLocaleLowerCase('fr-CH').includes(needle));
    });
}

export function visibleManualCatalogCandidates(catalog, labChoice, query, selected = {}) {
  const matching = manualCatalogCandidates(catalog, labChoice, query);
  const matchingCodes = new Set(matching.map(test => test.code));
  const pinned = manualCatalogCandidates(catalog, labChoice, '')
    .filter(test => selected[test.code] && !matchingCodes.has(test.code));
  return [...pinned, ...matching];
}

export function manualSelectionEntry(test) {
  const cost = Number(test?.cost_anissa_chf);
  if (!test?.code || !test?.display_name || !ALLOWED_LABS.has(test?.source_lab)
    || !Number.isFinite(cost) || cost < 0) return null;
  return {
    code: test.code,
    display_name: test.display_name,
    cost_anissa_chf: cost,
    category: test.category,
    source_lab: test.source_lab,
    justification: 'Sélection manuelle par Anissa',
    recommended_default: false,
  };
}

export default function AnalysisSuggestionModal({
  isOpen,
  onClose,
  client,
  packType,
  onValidate,  // async (plan) => {} — appele a la validation
}) {
  const modalTitleId = useId();
  const modalRef = useRef(null);
  const closeButtonRef = useRef(null);
  const suggestionRequestRef = useRef(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [iaResult, setIaResult] = useState(null);     // { client_summary, suggestions, alerts_anissa }
  const [enrichedSuggestions, setEnrichedSuggestions] = useState([]);
  const [selected, setSelected] = useState({});       // { code: true/false }
  const [extraTests, setExtraTests] = useState([]);   // tests ajoutés hors suggestions IA
  const [saving, setSaving] = useState(false);
  // V97.140 : nombre de fiches de commande saisi par Anissa, par labo
  // ({ ortho_analytic: '2' }). Le systeme l'estime par type de prelevement,
  // mais c'est elle qui remplit les fiches et qui tranche.
  //
  // On stocke la CHAINE brute, pas le nombre. Avec un `value` derive d'un
  // nombre, le champ ne peut jamais etre vide : un Backspace reaffiche
  // aussitot l'estimation, et le chiffre suivant se concatene (effacer 2 puis
  // taper 3 donnait 23, soit 496.80 CHF de taxe en silence). En gardant la
  // chaine, le champ peut rester vide pendant la frappe.
  const [sheetInputs, setSheetInputs] = useState({});
  // V97.143 : le labo retenu POUR CETTE CLIENTE. Anissa oriente sa cliente
  // vers Ortho, vers MGD, ou vers les deux — c'est sa decision, cliente par
  // cliente, et elle doit la poser AVANT que l'IA ne pioche.
  // 'both' par defaut : ne presume d'aucun labo tant qu'elle n'a pas tranche.
  const [labChoice, setLabChoice] = useState('both');
  // Labo avec lequel la suggestion affichee a ete generee. Si Anissa change
  // d'avis apres coup, on lui propose de relancer plutot que de rejouer un
  // appel IA a chaque clic.
  // null tant qu'aucune suggestion n'a ete generee.
  const [suggestedWithLab, setSuggestedWithLab] = useState(null);
  // Chargement du catalogue, distinct de l'appel IA (`loading`) : le premier
  // est automatique et gratuit, le second est declenche par Anissa et facture.
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [selectionMode, setSelectionMode] = useState('idle');
  const [manualQuery, setManualQuery] = useState('');
  // V97.12.4 : expand/collapse des notes internes (default collapse au-dela
  // de 3 alertes pour eviter le mur de texte).
  const [showAllAlerts, setShowAllAlerts] = useState(false);
  // Codes Ortho retenus, une fois le plan validé : la modale bascule alors
  // sur la liste des cases à cocher au lieu de se fermer.
  const [codesACocher, setCodesACocher] = useState(null);

  const packAccess = getPackAccess(packType);
  const pack = packAccess.known ? PACK_DEFINITIONS[packType] : null;
  const packPrice = pack?.price || 0;
  const creditChf = pack?.includedAnalysisCreditChf || 0;
  const validCatalog = useMemo(() => (
    catalog.filter(test => manualSelectionEntry(test) !== null)
  ), [catalog]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previousFocus = document.activeElement;
    const focusTimer = window.setTimeout(() => closeButtonRef.current?.focus(), 0);
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = [...(modalRef.current?.querySelectorAll(
        'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) || [])];
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault(); last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault(); first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      previousFocus?.focus?.();
    };
  }, [isOpen, onClose]);

  // ─── Chargement du catalogue (PAS de l'IA) ─────────────────────
  // V97.144 : l'IA ne part plus toute seule à l'ouverture. Elle partait avec
  // « Les deux » avant qu'Anissa n'ait choisi son labo : si elle voulait MGD,
  // le premier appel était déjà lancé et facturé pour rien. Le choix d'abord,
  // la génération ensuite.
  useEffect(() => {
    suggestionRequestRef.current += 1;
    setLoading(false);
    if (!isOpen || !client || !packType) return;
    let cancelled = false;
    if (!packAccess.known || !pack) {
      setCatalog([]);
      setCatalogLoading(false);
      setIaResult(null);
      setEnrichedSuggestions([]);
      setSelected({});
      setExtraTests([]);
      setError('Pack à vérifier avant de choisir des analyses');
      return undefined;
    }

    async function loadCatalog() {
      setCatalogLoading(true);
      setError(null);
      setIaResult(null);
      setEnrichedSuggestions([]);
      setSelected({});
      setExtraTests([]);
      // La modale reste montee entre deux ouvertures : sans ces resets, le
      // nombre de fiches et le labo saisis pour une cliente seraient
      // reappliques a la suivante, et factures comme si Anissa l'avait tranche.
      setSheetInputs({});
      setLabChoice('both');
      setSuggestedWithLab(null);
      setSelectionMode('idle');
      setManualQuery('');

      try {
        const { data: tests, error: catErr } = await supabase
          .from('lab_tests')
          .select('*')
          .eq('is_active', true)
          .order('category')
          .order('cost_anissa_chf');

        if (catErr) throw new Error(`Erreur chargement catalogue : ${catErr.message}`);
        if (cancelled) return;
        setCatalog(tests || []);
      } catch (err) {
        if (!cancelled) setError(err?.message || 'Erreur inattendue');
      } finally {
        if (!cancelled) setCatalogLoading(false);
      }
    }

    loadCatalog();
    return () => {
      cancelled = true;
      suggestionRequestRef.current += 1;
    };
  }, [isOpen, client?.id, packType, packAccess.known, pack]);

  // ─── Génération de la suggestion (déclenchée par Anissa) ───────
  async function runSuggestion() {
    if (!packAccess.known || !pack || !client?.id) {
      setError('Pack à vérifier avant de choisir des analyses');
      return;
    }
    const requestId = suggestionRequestRef.current + 1;
    suggestionRequestRef.current = requestId;
    setSelectionMode('ai');
    setLoading(true);
    setError(null);
    setIaResult(null);
    // Toujours remis a zero AVEC iaResult : les deux decrivent le meme fait
    // (« une suggestion est affichee, generee avec tel labo »). Les desynchroniser
    // enferme Anissa : relance echouee sur MGD -> suggestedWithLab reste 'ortho'
    // -> elle reclique Ortho pour revenir -> labChoice === suggestedWithLab ->
    // le bouton disparait, l'ecran ne montre qu'une erreur, plus aucune sortie.
    setSuggestedWithLab(null);
    setEnrichedSuggestions([]);
    setSelected({});
    setExtraTests([]);
    setSheetInputs({});
    try {
      // L'IA ne pioche que dans le(s) labo(s) retenu(s) pour cette cliente.
      // Filtrer AVANT l'appel et pas a l'affichage : sinon elle proposerait
      // des analyses d'un labo ecarte, et il faudrait les masquer apres coup
      // — un appel gaspille et des suggestions fantomes.
      const forAi = labChoice === 'both'
        ? validCatalog
        : validCatalog.filter(t => t.source_lab === labChoice);

      const result = await suggestAnalyses({ client, packType, catalog: forAi });

      if (suggestionRequestRef.current !== requestId) return;

      if (!result || !Array.isArray(result.suggestions)) {
        throw new Error('Réponse de préparation invalide ou vide');
      }

      const enriched = enrichSuggestionsWithCatalog(result.suggestions, validCatalog);
      setIaResult(result);
      setEnrichedSuggestions(enriched);
      setSuggestedWithLab(labChoice);

      // Aucune suggestion clinique n'est acceptee par defaut. Anissa choisit
      // explicitement chaque analyse apres lecture de la proposition.
      setSelected({});
    } catch (err) {
      if (suggestionRequestRef.current === requestId) {
        setError(err?.message || 'Erreur inattendue');
      }
    } finally {
      if (suggestionRequestRef.current === requestId) setLoading(false);
    }
  }

  // V97.143 : ecarter un labo doit AUSSI decocher ce qui venait de lui.
  // Sans ca, une analyse Ortho cochee puis masquee par le filtre resterait
  // dans le plan valide — invisible a l'ecran, bien presente sur la facture.
  useEffect(() => {
    suggestionRequestRef.current += 1;
    setLoading(false);
    setSheetInputs({});
    if (labChoice === 'both') return;
    const labOf = (code) => validCatalog.find(c => c.code === code)?.source_lab;
    setSelected(prev => {
      const next = {};
      for (const [code, on] of Object.entries(prev)) {
        if (on && labOf(code) === labChoice) next[code] = true;
      }
      return next;
    });
    setExtraTests(prev => prev.filter(t => (t.source_lab || labOf(t.code)) === labChoice));
  }, [labChoice, validCatalog]);

  // ─── Calcul cout + credit flat + couverture par card ───────────
  // V97.12.3 : le credit pack (0 / 250 / 400 CHF) est applique en
  // deduction monetaire FLAT sur le total. Pour l'affichage des badges
  // "Incluse dans l'accompagnement", on suit l'ordre explicite de la liste,
  // sans score ni hiérarchie clinique automatique.
  const { totalCost, analysesCost, labFees, selectedTests, creditApplied, costForClient, coveredCodes, totalMargin } = useMemo(() => {
    const allSelected = [];
    const seen = new Set();
    for (const candidate of [...enrichedSuggestions, ...extraTests]) {
      if (!selected[candidate?.code] || seen.has(candidate?.code)) continue;
      const canonical = manualSelectionEntry(validCatalog.find(test => test.code === candidate.code));
      if (!canonical || (labChoice !== 'both' && canonical.source_lab !== labChoice)) continue;
      seen.add(canonical.code);
      allSelected.push({
        ...canonical,
        justification: candidate.justification || canonical.justification,
      });
    }

    // V97.140 : ni les suggestions IA ni les ajouts manuels ne transportent
    // tax_chf/specimen_type — les deux chemins ne recopient qu'une partie des
    // champs. On les relit donc depuis `catalog`, seule source de verite, au
    // lieu d'elargir deux mappings qui redivergeront.
    const fees = computeLabFees(
      allSelected.map(t => {
        const ref = validCatalog.find(c => c.code === t.code);
        return {
          code: t.code,
          source_lab: t.source_lab || ref?.source_lab,
          specimen_type: ref?.specimen_type,
          tax_chf: ref?.tax_chf,
          // V97.145 : Ortho facture par fiche, MGD par prescription. Sans ce
          // champ, une cliente MGD sang + selles paierait 33.20 au lieu de 16.60.
          fee_model: ref?.fee_model,
        };
      }),
      // Seuls des entiers courts sont acceptes comme override ; tout le reste
      // retombe sur l'estimation. `type="number"` laisse passer plus qu'il n'y
      // parait : "2.5" afficherait "2.5 x 21.6 = 43.2" (faux a l'ecran), et
      // "1e5" est une saisie valide qui donnerait 2 160 000 CHF de taxe. Le
      // champ vide passe ici aussi, le temps qu'Anissa retape.
      {
        // Chez Ortho, ce qui coûte 21.60 est le BON envoyé, pas le type de
        // prélèvement. Le catalogue des bons sait sur lequel tombe chaque
        // analyse ; il rend null s'il ne connaît pas un test, et on retombe
        // alors sur l'ancienne estimation, qui ne sous-facture jamais.
        sheetEstimator: (lab, codes) => (
          lab === 'ortho_analytic' ? nombreDeBonsPour(codes) : null
        ),
        sheetOverrides: Object.fromEntries(
          Object.entries(sheetInputs)
            .filter(([, v]) => /^\d{1,3}$/.test(String(v).trim()))
            .map(([lab, v]) => [lab, Number(v)])
        ),
      }
    );

    // Le total facture inclut les taxes de fiche : Anissa avance les frais
    // labo et refacture la cliente, une taxe non comptee est une perte seche.
    //
    // 2026-08-22 — arrondi au franc superieur, ligne par ligne. Les prix
    // d'Ortho tombent sur des centimes (395.90, 300.90, 21.60) et une facture
    // a 417.50 se lit mal. On arrondit CE QUI EST FACTURE, jamais
    // `cost_anissa_chf` : ce champ reste la valeur exacte payee au labo, donc
    // la piece justificative et la base pour verifier une facture Ortho.
    //
    // L'ecart est de quelques francs par prescription, toujours en faveur
    // d'Anissa. « Arrondi au franc superieur » s'assume a voix haute ; un
    // arrondi vers le bas lui ferait perdre de l'argent en silence.
    // Le calcul vit dans calculerCoutFacture, en haut de ce fichier, pour
    // rester vérifiable : les montants sont avancés par Anissa puis
    // refacturés, une erreur ici est une perte sèche.
    // ⚠ totalFrais, pas totalTax : le second ignore les 5.90 de prelevement
    // sanguin que MGD facture sur toute commande de sang. Facture GEI0253959
    // du 2026-08-17. Ce qu'on n'ajoute pas ici, Anissa ne le refacture pas,
    // donc elle le paie.
    const montants = calculerCoutFacture(allSelected, fees.totalFrais, creditChf);

    return {
      totalCost: montants.totalCost,
      analysesCost: montants.analysesCost,
      labFees: fees,
      selectedTests: allSelected,
      creditApplied: montants.creditApplied,
      costForClient: montants.costForClient,
      coveredCodes: montants.coveredCodes,
      // V97.12.1 : champ deprecate mais conserve pour compat schema BDD.
      // Les couts etant arrondis au franc, la marge l'est aussi : le pack est
      // un prix rond, donc pas de bruit flottant a neutraliser.
      totalMargin: packPrice - montants.totalCost,
    };
  }, [selected, enrichedSuggestions, extraTests, creditChf, packPrice, validCatalog, sheetInputs, labChoice]);

  // ─── Tests disponibles hors suggestions (pour ajout manuel) ────
  const availableExtraTests = useMemo(() => {
    const suggestedCodes = new Set(enrichedSuggestions.map(s => s.code));
    const extraCodes = new Set(extraTests.map(s => s.code));
    return validCatalog.filter(t =>
      !suggestedCodes.has(t.code) &&
      !extraCodes.has(t.code) &&
      // V97.143 : on ne propose pas a l'ajout manuel un labo qu'Anissa a
      // ecarte pour cette cliente.
      (labChoice === 'both' || t.source_lab === labChoice)
    );
  }, [validCatalog, enrichedSuggestions, extraTests, labChoice]);

  // V97.143 : suggestions effectivement montrees. Si Anissa restreint le labo
  // apres coup, les propositions de l'autre labo sortent de l'ecran — elles
  // sont deja decochees par l'effet ci-dessus.
  const visibleSuggestions = useMemo(() => (
    labChoice === 'both'
      ? enrichedSuggestions
      : enrichedSuggestions.filter(s => s.source_lab === labChoice)
  ), [enrichedSuggestions, labChoice]);

  // V97.145 : le libelle de la ligne de frais depend du modele des labos
  // retenus. « N fiches » ne veut rien dire sur un forfait par prescription,
  // et un plan mixte Ortho + MGD cumule les deux logiques — dans ce cas on
  // reste generique plutot que d'en privilegier une.
  const labFeesLabel = useMemo(() => {
    const billing = labFees.labs.filter(l => l.taxUnit > 0);
    if (billing.length === 0) return 'Frais de laboratoire';
    const onlyPrescription = billing.every(l => l.feeModel === PER_PRESCRIPTION);
    if (onlyPrescription) {
      // 25.08.2026 — disait « Forfait de prescription » au singulier, quel
      // que soit le nombre de commandes. Sur le cycle de Clelia il y en avait
      // deux, sang et salive, et la ligne en annoncait une. Le compte doit se
      // lire, c'est lui qui se trompe le plus cher.
      const n = billing.reduce((somme, l) => somme + l.sheetCount, 0);
      return `Taxes de commande (${n} commande${n > 1 ? 's' : ''})`;
    }
    const onlySheets = billing.every(l => l.feeModel !== PER_PRESCRIPTION);
    if (onlySheets) {
      const n = labFees.taxedSheets;
      return `Taxes de fiche (${n} fiche${n > 1 ? 's' : ''})`;
    }
    return 'Frais de laboratoire';
  }, [labFees]);

  // Ce que l'IA aura reellement le droit de proposer avec le labo courant.
  // Sert le compteur avant generation : afficher tout le catalogue alors
  // qu'Anissa a restreint a MGD annoncerait des analyses hors de portee.
  const catalogForChoice = useMemo(() => (
    labChoice === 'both' ? validCatalog : validCatalog.filter(t => t.source_lab === labChoice)
  ), [validCatalog, labChoice]);

  const manualCandidates = useMemo(() => (
    visibleManualCatalogCandidates(validCatalog, labChoice, manualQuery, selected)
  ), [validCatalog, labChoice, manualQuery, selected]);

  // ─── Handlers ──────────────────────────────────────────────────
  function toggleSelected(code) {
    setSelected(prev => ({ ...prev, [code]: !prev[code] }));
  }

  function addExtraTest(code) {
    const test = validCatalog.find(t => t.code === code);
    if (!test) return;
    setExtraTests(prev => [...prev, {
      code: test.code,
      display_name: test.display_name,
      cost_anissa_chf: test.cost_anissa_chf,
      category: test.category,
      source_lab: test.source_lab,
      justification: 'Ajout manuel par Anissa',
      recommended_default: false,
    }]);
    setSelected(prev => ({ ...prev, [code]: true }));
  }

  function startManualSelection() {
    if (selectionMode === 'manual') return;
    suggestionRequestRef.current += 1;
    setSelectionMode('manual');
    setIaResult(null);
    setSuggestedWithLab(null);
    setEnrichedSuggestions([]);
    setSelected({});
    setExtraTests([]);
    setSheetInputs({});
    setError(null);
  }

  function toggleManualTest(test) {
    const entry = manualSelectionEntry(test);
    if (!entry) return;
    setExtraTests(prev => prev.some(item => item.code === entry.code) ? prev : [...prev, entry]);
    toggleSelected(entry.code);
  }

  async function handleValidate() {
    if (!packAccess.known || !pack) {
      setError('Pack à vérifier avant de choisir des analyses');
      return;
    }
    if (selectedTests.length === 0) {
      setError('Selectionnez au moins une analyse');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const plan = {
        client_id: client.id,
        pack_type: packType,
        pack_price_chf: packPrice,
        selected_tests: selectedTests.map(t => ({
          code: t.code,
          name: t.display_name,
          cost_anissa_chf: t.cost_anissa_chf,
          category: t.category,
          source_lab: t.source_lab,
          status: 'recommended',
          reason: t.justification,
        })),
        total_cost_anissa_chf: totalCost,
        // V97.140 : total_cost_anissa_chf inclut les taxes de fiche. On trace
        // leur detail pour que le montant reste reconciliable avec la facture
        // du labo, et pour garder ce qu'Anissa a retenu comme nombre de fiches.
        lab_fees: {
          labs: labFees.labs.map(l => ({
            lab: l.lab,
            taxUnit: l.taxUnit,
            sheetCount: l.sheetCount,
            estimatedSheets: l.estimatedSheets,
            isOverridden: l.isOverridden,
            tax: l.tax,
            prelevement: l.prelevement,
            specimens: l.specimens,
          })),
          totalTax: labFees.totalTax,
          // 25.08.2026 — sans ces deux lignes, relire un plan ne permet plus
          // de retrouver la facture : il manquerait les 5.90 de prelevement,
          // et rien ne dirait pourquoi le total depasse la somme des taxes.
          totalPrelevement: labFees.totalPrelevement,
          totalFrais: labFees.totalFrais,
          totalSheets: labFees.totalSheets,
          // Sans taxedSheets, la donnee persistee redirait ce qu'on vient de
          // retirer de l'ecran : { totalTax: 21.6, totalSheets: 2 } pour une
          // fiche Ortho + une fiche MGD gratuite. A la relecture, le montant
          // ne serait plus reconciliable avec la facture du labo.
          taxedSheets: labFees.taxedSheets,
        },
        // 25.08.2026 — LE MONTANT QU'ANISSA DOIT REFACTURER À LA CLIENTE.
        //
        // Il etait calcule a l'ecran au moment de la prescription, affiche
        // une fois, puis perdu : la table ne gardait que le cout et une
        // pseudo-marge. Des semaines plus tard, la facture du labo arrive et
        // il faut refaire le calcul de tete. Sur le cycle de Clelia, refaire
        // ce calcul de memoire sur la prevision au lieu du reel coutait
        // 131.10 CHF de perte seche, invisible.
        //
        // Anissa avance les frais puis refacture : ce chiffre EST la
        // prestation financiere. Il se stocke.
        cost_for_client_chf: costForClient,
        credit_applied_chf: creditApplied,
        // ⚠ Champ deprecate. Il vaut prix du pack moins cout labo, ce qui ne
        // veut rien dire dans le modele V3.1 : les packs sont du temps humain
        // et ne portent pas de marge. Conserve pour la compat du schema, il
        // ne doit servir a aucune decision. Voir packSystem.js.
        total_margin_chf: totalMargin,
        notes_anissa: iaResult?.alerts_anissa?.join(' • ') || null,
        status: 'draft',
      };
      await onValidate(plan);
      // 22.08.2026 — la modale ne se ferme plus tout de suite quand des
      // analyses Ortho sont retenues. Anissa a les bons papier devant elle,
      // devant la cliente : c'est le moment de lui dire quoi cocher, pas
      // après avoir tout refermé.
      const codesOrtho = selectedTests
        .filter((t) => t.source_lab === 'ortho_analytic')
        .map((t) => t.code);
      if (codesOrtho.length > 0) {
        setCodesACocher(codesOrtho);
        return;
      }
      onClose();
    } catch (err) {
      setError(err?.message || 'Erreur sauvegarde plan');
    } finally {
      setSaving(false);
    }
  }

  if (!isOpen) return null;

  // ─── Étape « quoi cocher sur le papier » ───────────────────────
  // Le plan est enregistré, Anissa a les bons devant elle et la cliente en
  // face. On ne referme pas avant de lui avoir dit quoi cocher.
  if (codesACocher) {
    return (
      <div className="modal-overlay" style={overlayStyle} onClick={onClose}>
        <div
          ref={modalRef}
          className="modal-content"
          style={contentStyle}
          role="dialog"
          aria-modal="true"
          aria-labelledby={modalTitleId}
          onClick={e => e.stopPropagation()}
        >
          <div style={headerStyle}>
            <div>
              <h2 id={modalTitleId} style={{ margin: 0, fontSize: 18 }}>À cocher sur les bons Ortho</h2>
              <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>
                Analyses enregistrées pour {client?.form?.prenom || 'la cliente'}.
              </p>
            </div>
            <button ref={closeButtonRef} onClick={onClose} style={closeBtnStyle} aria-label="Fermer">×</button>
          </div>

          <div style={bodyStyle}>
            <BonsACocher codes={codesACocher} />
          </div>

          <div style={footerStyle}>
            <button type="button" onClick={onClose} style={btnPrimaryStyle}>
              Bons remplis, fermer
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── RENDER ────────────────────────────────────────────────────
  return (
    <div className="modal-overlay" style={overlayStyle} onClick={onClose}>
      <div
        ref={modalRef}
        className="modal-content"
        style={contentStyle}
        role="dialog"
        aria-modal="true"
        aria-labelledby={modalTitleId}
        onClick={e => e.stopPropagation()}
      >
        <div style={headerStyle}>
          <div>
            <h2 id={modalTitleId} style={{ margin: 0, fontSize: 18 }}>Choisir les analyses</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#888' }}>
              {client?.form?.prenom || 'Cliente'} • Pack : <strong>{pack?.label}</strong> ({packPrice} CHF)
            </p>
          </div>
          <button ref={closeButtonRef} onClick={onClose} style={closeBtnStyle} aria-label="Fermer">×</button>
        </div>

        <div style={bodyStyle}>
          {/* V97.143 — vers quel laboratoire cette cliente est orientée.
              Ortho-Analytic et MGD sont deux partenaires à égalité : le choix
              appartient à Anissa, cliente par cliente, et il conditionne ce
              que la préparation a le droit de proposer. « Les deux » par défaut : on ne
              présume d'aucun labo tant qu'elle n'a pas tranché. */}
          <div style={labChoiceStyle}>
            <span style={{ fontSize: 12, color: '#555' }}>Laboratoire pour cette cliente :</span>
            <div style={{ display: 'inline-flex', gap: 4 }} role="group" aria-label="Laboratoire retenu">
              {LAB_CHOICES.map(c => {
                const active = labChoice === c.value;
                return (
                  <button
                    key={c.value}
                    type="button"
                    onClick={() => setLabChoice(c.value)}
                    aria-pressed={active}
                    // Actif en vert SOURD, pas en vert plein : le plein est
                    // reserve a l'action (« Generer »), qui vit dans la meme
                    // barre. Deux verts pleins cote a cote melangeraient un
                    // etat selectionne et une action a faire.
                    style={{
                      fontSize: 12,
                      fontWeight: active ? 600 : 500,
                      padding: '4px 12px',
                      borderRadius: 5,
                      cursor: 'pointer',
                      border: `1px solid ${active ? '#2d5a3d' : 'rgba(0,0,0,0.18)'}`,
                      background: active ? 'rgba(45,90,61,0.12)' : '#fff',
                      color: active ? '#2d5a3d' : '#555',
                    }}
                  >
                    {c.label}
                  </button>
                );
              })}
            </div>
            {!loading && !catalogLoading && (
              <button
                type="button"
                onClick={startManualSelection}
                aria-pressed={selectionMode === 'manual'}
                disabled={catalogForChoice.length === 0}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '5px 14px',
                  borderRadius: 5,
                  cursor: catalogForChoice.length === 0 ? 'not-allowed' : 'pointer',
                  border: '1px solid rgba(45,90,61,0.4)',
                  background: selectionMode === 'manual' ? 'rgba(45,90,61,0.12)' : '#fff',
                  color: '#2d5a3d',
                  opacity: catalogForChoice.length === 0 ? 0.5 : 1,
                }}
              >
                Choisir manuellement
              </button>
            )}
            {/* Chaque génération est un appel IA facturé : c'est Anissa qui la
                déclenche, jamais un clic sur le sélecteur ni l'ouverture de la
                modale. Le bouton reste tant que le labo affiché ne correspond
                pas au labo choisi. */}
            {!loading && !catalogLoading && labChoice !== suggestedWithLab && (
              <button
                type="button"
                onClick={runSuggestion}
                disabled={catalogForChoice.length === 0}
                style={{
                  fontSize: 12,
                  fontWeight: 600,
                  padding: '5px 14px',
                  borderRadius: 5,
                  // Pousse l'action a droite : elle ne fait pas partie du
                  // groupe de choix, elle agit dessus.
                  marginLeft: 'auto',
                  cursor: catalogForChoice.length === 0 ? 'not-allowed' : 'pointer',
                  border: '1px solid #2d5a3d',
                  background: iaResult ? 'transparent' : '#2d5a3d',
                  color: iaResult ? '#2d5a3d' : '#fff',
                  opacity: catalogForChoice.length === 0 ? 0.5 : 1,
                }}
              >
                {iaResult ? 'Relancer la suggestion' : 'Générer la suggestion'}
              </button>
            )}
          </div>

          {/* Aucune suggestion encore demandée : on dit quoi faire plutôt que
              de laisser un écran vide sous le sélecteur. */}
          {!loading && !catalogLoading && selectionMode === 'idle' && !iaResult && !error && (
            <div style={{ ...loadingStyle, color: '#888' }}>
              <p style={{ margin: 0 }}>Choisis le laboratoire, puis sélectionne directement les analyses ou demande une suggestion.</p>
              <p style={{ fontSize: 12, marginTop: 4 }}>
                {catalogForChoice.length} analyse{catalogForChoice.length > 1 ? 's' : ''} disponible{catalogForChoice.length > 1 ? 's' : ''}
                {labChoice !== 'both' && ` chez ${LAB_CHOICES.find(c => c.value === labChoice)?.label}`}
              </p>
            </div>
          )}

          {!loading && !catalogLoading && selectionMode === 'manual' && (
            <section aria-label="Sélection manuelle des analyses">
              <div style={manualHeaderStyle}>
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase', color: '#2d5a3d' }}>
                    Sélection manuelle
                  </div>
                  <p style={{ margin: '4px 0 0', fontSize: 13, color: '#555' }}>
                    Aucune analyse n'est cochée par défaut. Anissa choisit chaque ligne explicitement.
                  </p>
                </div>
                <input
                  type="search"
                  value={manualQuery}
                  onChange={event => setManualQuery(event.target.value)}
                  placeholder="Rechercher une analyse…"
                  aria-label="Rechercher dans le catalogue d'analyses"
                  style={manualSearchStyle}
                />
              </div>
              <div style={{ fontSize: 12, color: '#777', margin: '10px 0 8px' }} aria-live="polite">
                {manualCandidates.length} analyse{manualCandidates.length > 1 ? 's' : ''} affichée{manualCandidates.length > 1 ? 's' : ''}
                {' · '}{selectedTests.length} sélectionnée{selectedTests.length > 1 ? 's' : ''}
              </div>
              {manualCandidates.length === 0 ? (
                <div style={{ ...loadingStyle, padding: '24px 16px', color: '#888' }}>Aucune analyse ne correspond à cette recherche.</div>
              ) : manualCandidates.map(test => (
                <SuggestionRow
                  key={test.code}
                  suggestion={manualSelectionEntry(test)}
                  selected={!!selected[test.code]}
                  onToggle={() => toggleManualTest(test)}
                  isExtra
                  isManual
                  isCovered={coveredCodes.has(test.code)}
                />
              ))}
            </section>
          )}

          {loading && (
            <div style={loadingStyle}>
              <p>Analyse algorithmique de l'anamnèse en cours…</p>
              <p style={{ fontSize: 12, color: '#888' }}>~5-10 secondes</p>
            </div>
          )}

          {error && (
            <div style={errorStyle}>
              <strong>Erreur :</strong> {error}
            </div>
          )}

          {!loading && iaResult && (
            <>
              <div style={summaryStyle}>
                <div style={{
                  fontSize: 11,
                  fontWeight: 600,
                  letterSpacing: '.12em',
                  textTransform: 'uppercase',
                  color: '#2d5a3d',
                  marginBottom: 8,
                }}>
                  Lecture clinique synthétique
                </div>

                {/* V97.12.5 : axes principaux en bullets compacts (max 3),
                    puis contexte en paragraphe. Si la préparation ne sort pas d’axes
                    (anciens plans / fallback), on rebascule sur paragraphe seul. */}
                {Array.isArray(iaResult.axes_principaux) && iaResult.axes_principaux.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div style={{
                      fontSize: 10,
                      fontWeight: 700,
                      letterSpacing: '.14em',
                      textTransform: 'uppercase',
                      color: '#6b7d6b',
                      marginBottom: 4,
                    }}>
                      Axes principaux
                    </div>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none' }}>
                      {iaResult.axes_principaux.slice(0, 3).map((axe, i) => (
                        <li key={i} style={{
                          fontSize: 14,
                          color: '#1a2e1f',
                          padding: '3px 0',
                          paddingLeft: 16,
                          position: 'relative',
                          lineHeight: 1.45,
                          fontWeight: 500,
                        }}>
                          <span style={{
                            position: 'absolute',
                            left: 0,
                            top: 3,
                            color: '#2d5a3d',
                            fontWeight: 700,
                          }}>
                            {i + 1}.
                          </span>
                          {axe}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {iaResult.client_summary && (
                  <>
                    {Array.isArray(iaResult.axes_principaux) && iaResult.axes_principaux.length > 0 && (
                      <div style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '.14em',
                        textTransform: 'uppercase',
                        color: '#6b7d6b',
                        marginBottom: 4,
                      }}>
                        Contexte
                      </div>
                    )}
                    <p style={{ margin: 0, color: '#1a2e1f', lineHeight: 1.65, fontSize: 14 }}>
                      {iaResult.client_summary}
                    </p>
                  </>
                )}
              </div>

              <h3 style={{ marginTop: 16, fontSize: 14 }}>Analyses suggérées</h3>
              <div>
                {visibleSuggestions.map(s => (
                  <SuggestionRow
                    key={s.code}
                    suggestion={s}
                    selected={!!selected[s.code]}
                    onToggle={() => toggleSelected(s.code)}
                    isCovered={coveredCodes.has(s.code)}
                  />
                ))}
                {extraTests.map(s => (
                  <SuggestionRow
                    key={s.code}
                    suggestion={s}
                    selected={!!selected[s.code]}
                    onToggle={() => toggleSelected(s.code)}
                    isExtra
                    isCovered={coveredCodes.has(s.code)}
                  />
                ))}
              </div>

              {availableExtraTests.length > 0 && (
                <div style={addExtraStyle}>
                  <label style={{ fontSize: 12, color: '#666' }}>+ Ajouter une analyse hors suggestions :</label>
                  <select
                    onChange={e => { if (e.target.value) addExtraTest(e.target.value); e.target.value = ''; }}
                    style={{ marginLeft: 8, padding: '4px 8px', maxWidth: 320 }}
                  >
                    <option value="">— Choisir —</option>
                    {availableExtraTests.map(t => (
                      <option key={t.code} value={t.code}>
                        {t.display_name} ({Math.ceil(Number(t.cost_anissa_chf) || 0)} CHF)
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {iaResult.alerts_anissa?.length > 0 && (() => {
                // V97.12.4 : section renommee "Notes internes Anissa".
                // Important compliance : ces notes ne doivent PAS apparaitre
                // dans les exports cliente (PDF programme, app cliente, etc.).
                // Default collapse au-dela de 3 pour eviter le mur de texte
                // sur cas complexes.
                const alerts = iaResult.alerts_anissa;
                const MAX_VISIBLE = 3;
                const hidden = Math.max(0, alerts.length - MAX_VISIBLE);
                const visible = showAllAlerts ? alerts : alerts.slice(0, MAX_VISIBLE);
                return (
                  <div style={alertsStyle}>
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      fontSize: 11,
                      fontWeight: 600,
                      letterSpacing: '.1em',
                      textTransform: 'uppercase',
                      color: '#6b5018',
                      marginBottom: 4,
                    }}>
                      Notes internes Anissa
                      <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, color: '#9b7d3a', fontSize: 11 }}>
                        — non transmises à la cliente
                      </span>
                    </div>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                      {visible.map((a, i) => <li key={i}>{a}</li>)}
                    </ul>
                    {hidden > 0 && (
                      <button
                        type="button"
                        onClick={() => setShowAllAlerts(v => !v)}
                        style={{
                          marginTop: 8,
                          background: 'transparent',
                          border: 'none',
                          color: '#6b5018',
                          fontSize: 12,
                          fontWeight: 500,
                          cursor: 'pointer',
                          padding: 0,
                          textDecoration: 'underline',
                        }}
                      >
                        {showAllAlerts ? '— Replier' : `+ ${hidden} observation${hidden > 1 ? 's' : ''} complémentaire${hidden > 1 ? 's' : ''}`}
                      </button>
                    )}
                  </div>
                );
              })()}
            </>
          )}
        </div>

        {!loading && (iaResult || selectionMode === 'manual') && (
          <div style={footerStyle}>
            <div style={{ flex: 1 }}>
              {/* V97.12.2 — Credit pack applique en deduction monetaire flat
                  sur le total des analyses (peut couvrir 1 ou 2 analyses
                  selon les couts, jusqu'au plafond du credit). */}
              <div style={{ fontSize: 12, color: '#666', marginBottom: 2 }}>
                Pack accompagnement : <strong>{packPrice} CHF</strong>
                {creditChf > 0 && (
                  <> · Crédit analyse inclus : <strong>{creditChf} CHF</strong></>
                )}
              </div>
              {/* V97.140 : les fiches de commande. Le nombre de fiches pilote
                  la taxe, et c'est Anissa qui remplit les fiches : on pre-remplit
                  notre estimation (un type de prelevement = une fiche) et elle
                  corrige. Le champ n'apparait que pour un labo qui taxe.
                  Le bloc est place AVANT le decompte : c'est le levier qui
                  alimente la ligne "Taxes de fiche" juste en dessous.
                  Volontairement SANS titre : "Fiches de commande remplies :"
                  juste dessous et "Taxes de fiche" dans le decompte disent deja
                  la meme chose. Un eyebrow ferait un troisieme enonce du meme
                  concept en 100px de haut, et le footer n'a pas de budget
                  vertical a depenser (il est hors du scroll du corps). */}
              {selectedTests.length > 0 && labFees.labs.some(l => l.taxUnit > 0) && (
                <div style={sheetBlockStyle}>
                  {labFees.labs.filter(l => l.taxUnit > 0).map(l => (
                    <div key={l.lab} style={{ display: 'grid', gridTemplateColumns: '56px 1fr', columnGap: 8, marginBottom: 6 }}>
                      <div style={{ paddingTop: 4 }}>
                        <LabBadge sourceLab={l.lab} />
                      </div>
                      {/* V97.145 : MGD facture un forfait UNIQUE par
                          prescription — il n'y a pas de fiche a compter, donc
                          rien a saisir. Demander un nombre ici laisserait
                          croire qu'il change le montant. */}
                      {l.feeModel === PER_PRESCRIPTION ? (
                        <div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span>Forfait prescription :</span>
                            <span>= <strong style={{ color: '#1a2e1f' }}>{l.tax} CHF</strong></span>
                          </div>
                          <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                            une seule fois, quel que soit le nombre d’analyses
                          </div>
                        </div>
                      ) : (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                          <span>Fiches de commande remplies :</span>
                          <input
                            type="number"
                            min="0"
                            step="1"
                            aria-label={`Nombre de fiches de commande ${LAB_BADGES[l.lab]?.label || l.lab}`}
                            // `??` et non `||` : la chaine vide doit rester vide
                            // pendant la frappe, sinon on retombe dans la
                            // concatenation.
                            value={sheetInputs[l.lab] ?? l.sheetCount}
                            onChange={e => setSheetInputs(prev => ({ ...prev, [l.lab]: e.target.value }))}
                            // Champ laisse vide : on relache l'override pour
                            // reafficher l'estimation plutot qu'un champ orphelin.
                            onBlur={() => setSheetInputs(prev => {
                              if (prev[l.lab] !== '') return prev;
                              const next = { ...prev };
                              delete next[l.lab];
                              return next;
                            })}
                            style={{
                              width: 56,
                              padding: '4px 6px',
                              fontSize: 13,
                              fontWeight: 600,
                              textAlign: 'center',
                              color: '#1a2e1f',
                              background: '#fff',
                              border: '1px solid rgba(0,0,0,0.25)',
                              borderRadius: 4,
                            }}
                          />
                          <span style={{ color: '#888' }}>× {l.taxUnit} CHF</span>
                          <span>= <strong style={{ color: '#1a2e1f' }}>{l.tax} CHF</strong></span>
                        </div>
                        <div style={{ fontSize: 11, color: '#888', marginTop: 2 }}>
                          {l.specimens.filter(s => s !== UNKNOWN_SPECIMEN).join(', ') || 'prélèvement non renseigné'}
                          {l.isOverridden && (
                            <> · estimation : {l.estimatedSheets}</>
                          )}
                        </div>
                      </div>
                      )}
                    </div>
                  ))}
                  {/* N'alerter que si le prelevement change vraiment quelque
                      chose : sur un forfait par prescription, il n'entre pas
                      dans le calcul, l'ignorer est sans consequence. */}
                  {labFees.labs.some(l => l.taxUnit > 0 && l.feeModel !== PER_PRESCRIPTION && l.specimens.includes(UNKNOWN_SPECIMEN)) && (
                    <div style={{ fontSize: 12, color: '#856404', marginTop: 2 }}>
                      Le type de prélèvement manque sur une analyse : le nombre de fiches
                      proposé peut être faux. À vérifier avant de commander.
                    </div>
                  )}
                </div>
              )}
              {selectedTests.length > 0 && (
                <div style={{ fontSize: 12, color: '#555', marginBottom: 8, maxWidth: LEDGER_WIDTH }}>
                  {labFees.totalFrais > 0 && (
                    <>
                      <div style={ledgerRowStyle}>
                        <span>Analyses sélectionnées ({selectedTests.length})</span>
                        <span style={ledgerNumStyle}>{analysesCost} CHF</span>
                      </div>
                      {labFees.totalTax > 0 && (
                        <div style={ledgerRowStyle}>
                          {/* Le libelle suit le modele du/des labo(s) : parler de
                              « fiches » sur une taxe de commande serait faux. */}
                          <span>{labFeesLabel}</span>
                          <span style={ledgerNumStyle}>{labFees.totalTax} CHF</span>
                        </div>
                      )}
                      {labFees.totalPrelevement > 0 && (
                        <div style={ledgerRowStyle}>
                          {/* Ligne separee et nommee : c'est un poste que le
                              labo facture a part, et le voir ici evite de
                              redecouvrir 5.90 sur la facture six semaines
                              plus tard. */}
                          <span>Prélèvement sanguin</span>
                          <span style={ledgerNumStyle}>{labFees.totalPrelevement} CHF</span>
                        </div>
                      )}
                    </>
                  )}
                  <div style={{
                    ...ledgerRowStyle,
                    fontWeight: 600,
                    color: '#1a2e1f',
                    ...(labFees.totalFrais > 0
                      ? { borderTop: '1px solid rgba(0,0,0,0.1)', marginTop: 2, paddingTop: 4 }
                      : {}),
                  }}>
                    {/* Avec des taxes, le decompte porte deja une ligne
                        "Analyses selectionnees (3) … 410 CHF" : intituler ce
                        total "Total analyses selectionnees" a 453.20 CHF
                        donnerait deux fois les memes mots pour deux montants
                        differents. Sur un bloc d'argent, l'intitule doit dire
                        exactement ce que le chiffre recouvre. */}
                    <span>{labFees.totalFrais > 0 ? 'Total à commander' : `Total analyses sélectionnées (${selectedTests.length})`}</span>
                    <span style={ledgerNumStyle}>{totalCost} CHF</span>
                  </div>
                  {creditApplied > 0 && (
                    <div style={{ ...ledgerRowStyle, color: '#2d5a3d', fontWeight: 500 }}>
                      <span>Crédit appliqué</span>
                      <span style={ledgerNumStyle}>−{creditApplied} CHF</span>
                    </div>
                  )}
                </div>
              )}
              {/* V97.12.3 : conclusion economique aeree, vraie chute du cockpit */}
              <div style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: '1px dashed rgba(45,90,61,0.25)',
                maxWidth: LEDGER_WIDTH,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                gap: 16,
                fontSize: 19,
                fontWeight: 600,
                color: '#2d5a3d',
              }}>
                <span>Coût analyses pour la cliente</span>
                <span style={ledgerNumStyle}>{costForClient} CHF</span>
              </div>
            </div>
            <button onClick={onClose} style={btnSecondaryStyle} disabled={saving}>
              Annuler
            </button>
            <button
              onClick={handleValidate}
              style={btnPrimaryStyle}
              disabled={saving || selectedTests.length === 0}
            >
              {saving ? 'Enregistrement…' : `Valider le plan (${selectedTests.length} ${selectedTests.length > 1 ? 'analyses' : 'analyse'})`}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sous-composant : ligne de suggestion ───────────────────────
function SuggestionRow({ suggestion, selected, onToggle, isExtra, isManual = false, isCovered = false }) {
  const s = suggestion;
  return (
    <label style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 12,
      padding: 10,
      paddingTop: 10,
      marginTop: 0,
      marginBottom: 6,
      // Mise en évidence strictement financière : couverture par le crédit.
      background: isCovered
        ? 'rgba(45,90,61,0.12)'
        : selected ? 'rgba(45,90,61,0.08)' : 'rgba(0,0,0,0.02)',
      border: `1px solid ${
        isCovered ? 'rgba(45,90,61,0.4)' :
        selected ? 'rgba(45,90,61,0.3)' : 'rgba(0,0,0,0.08)'
      }`,
      borderRadius: 6,
      cursor: 'pointer',
      position: 'relative',
    }}>
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        aria-label={`Sélectionner ${s.display_name}`}
        style={{ marginTop: 4 }}
      />
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <strong style={{ fontSize: 14 }}>{s.display_name}</strong>
          <LabBadge sourceLab={s.source_lab} />
          <span style={{ fontSize: 12, color: '#666' }}>· {s.cost_anissa_chf} CHF</span>
          <span style={{ fontSize: 11, color: '#888', textTransform: 'uppercase' }}>· {s.category}</span>
          {isExtra && !isManual && (
            <span style={{ fontSize: 10, color: '#888', background: 'rgba(0,0,0,0.05)', padding: '2px 6px', borderRadius: 4 }}>
              Ajouté
            </span>
          )}
          {isManual && selected && (
            <span style={{ fontSize: 10, color: '#2d5a3d', background: 'rgba(45,90,61,0.1)', padding: '2px 6px', borderRadius: 4 }}>
              Sélectionnée
            </span>
          )}
          {isCovered && (
            <span style={{
              fontSize: 11,
              fontWeight: 600,
              color: '#fff',
              background: '#2d5a3d',
              padding: '3px 9px',
              borderRadius: 4,
              letterSpacing: '.02em',
            }}>
              ✓ Incluse dans l'accompagnement
            </span>
          )}
          {!isExtra && (
            <span style={{ fontSize: 11, color: '#6b7d6b', fontWeight: 500 }}>
              · Proposition à vérifier
            </span>
          )}
        </div>
        <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
          {s.justification}
        </div>
      </div>
    </label>
  );
}

// ─── Styles inline (simples, sans dépendance CSS) ────────────────
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000,
  display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
};
const contentStyle = {
  background: '#fff', borderRadius: 12, maxWidth: 760, width: '100%',
  maxHeight: '90vh', display: 'flex', flexDirection: 'column',
};
const headerStyle = {
  display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
  padding: '16px 20px', borderBottom: '1px solid rgba(0,0,0,0.08)',
};
const closeBtnStyle = {
  background: 'none', border: 'none', fontSize: 28, cursor: 'pointer',
  color: '#888', padding: 0, lineHeight: 1, marginLeft: 12,
};
const bodyStyle = {
  padding: 20, overflowY: 'auto', flex: 1,
};
const manualHeaderStyle = {
  display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap',
  padding: '14px 16px', marginTop: 14, borderRadius: 8,
  background: 'linear-gradient(135deg, rgba(45,90,61,0.09), rgba(244,239,226,0.65))',
  border: '1px solid rgba(45,90,61,0.18)',
};
const manualSearchStyle = {
  width: 230, maxWidth: '100%', flex: '1 1 220px', padding: '8px 10px', borderRadius: 6,
  border: '1px solid rgba(0,0,0,0.18)', background: '#fff', color: '#1a2e1f',
  fontSize: 13,
};
const loadingStyle = {
  textAlign: 'center', padding: '40px 20px',
};
const errorStyle = {
  background: 'rgba(196,68,68,0.1)', color: '#c44', padding: 12,
  borderRadius: 6, marginBottom: 12,
};
const summaryStyle = {
  // V97.12.3 : encart plus aere + titre eyebrow "Lecture clinique synthetique".
  // Avant : visuellement disabled (gris). Maintenant : presence claire avec
  // titre, fond sage subtil, contraste deep #1a2e1f.
  background: 'rgba(45,90,61,0.08)',
  border: '1px solid rgba(45,90,61,0.18)',
  padding: '14px 16px',
  borderRadius: 8,
  marginBottom: 16,
};
const alertsStyle = {
  background: 'rgba(255,193,7,0.1)', padding: 12, borderRadius: 6,
  fontSize: 13, marginTop: 12, color: '#856404',
};
const addExtraStyle = {
  marginTop: 12, padding: 10, background: 'rgba(0,0,0,0.02)',
  borderRadius: 6, display: 'flex', alignItems: 'center', flexWrap: 'wrap',
};
// V97.143 : le choix du labo se pose AVANT de lire les propositions, d'ou sa
// place en tete du corps de la modale.
const labChoiceStyle = {
  display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
  padding: '8px 10px', marginBottom: 12,
  background: 'rgba(0,0,0,0.02)', border: '1px solid rgba(0,0,0,0.07)',
  borderRadius: 6,
};
// V97.140 : recapitulatif economique du footer. Le bloc fiches est la zone
// d'action d'Anissa (fond gris leger, champ blanc = editable) ; le decompte
// dessous est une colonne de montants alignes a droite (tabular-nums) qui se
// termine sur la chute — meme bord droit pour que l'oeil suive la colonne
// jusqu'au montant final.
//
// Cet alignement des trois blocs est tout l'argument du bloc : la largeur doit
// donc vivre a UN seul endroit. Repetee en dur, elle finirait par diverger sur
// un seul des trois et casserait silencieusement la colonne.
const LEDGER_WIDTH = 420;
const sheetBlockStyle = {
  fontSize: 12, color: '#555', marginBottom: 8, padding: '8px 10px',
  background: 'rgba(0,0,0,0.03)', border: '1px solid rgba(0,0,0,0.07)',
  borderRadius: 6, maxWidth: LEDGER_WIDTH,
};
const ledgerRowStyle = {
  display: 'flex', justifyContent: 'space-between', gap: 16, padding: '2px 0',
};
const ledgerNumStyle = {
  fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap',
};
const footerStyle = {
  display: 'flex', alignItems: 'center', gap: 12, padding: 16,
  borderTop: '1px solid rgba(0,0,0,0.08)',
};
const btnSecondaryStyle = {
  padding: '10px 20px', background: 'transparent', border: '1px solid #ccc',
  borderRadius: 6, cursor: 'pointer', fontSize: 13,
};
const btnPrimaryStyle = {
  padding: '10px 20px', background: '#2d5a3d', color: '#fff', border: 'none',
  borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600,
};
